import { useEffect, useRef, useState } from "react";

import { useAuth } from "../lib/auth";
import Avatar from "./Avatar";
import "./UserMenu.css";

/** Who is signed in, and the way out. */
export default function UserMenu() {
  const { user, signOut } = useAuth();
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (wrap.current && !wrap.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // The app does not render the shell without a user, so this is only ever
  // hit for the instant between signing out and the login screen appearing.
  if (!user) return null;

  return (
    <div className="usermenu" ref={wrap}>
      <button
        className="usermenu__btn"
        type="button"
        aria-expanded={open}
        aria-label={`Signed in as ${user.name}`}
        // The rail is 192px wide, so a name of any length truncates in it.
        // This is the cheap way to read the whole one without opening the
        // panel to do it.
        title={user.name}
        onClick={() => setOpen((v) => !v)}
      >
        <Avatar name={user.name} size={28} />
        <span className="usermenu__name">{user.name}</span>
        <svg
          className="usermenu__chev"
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
        >
          <path
            d="M3 4.5 6 7.5 9 4.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && (
        <div className="usermenu__panel" role="dialog" aria-label="Account">
          <div className="usermenu__who">
            <Avatar name={user.name} size={36} />
            <span>
              <strong>{user.name}</strong>
              {user.email && <span className="caption">{user.email}</span>}
            </span>
          </div>
          <button
            className="usermenu__out"
            type="button"
            onClick={() => {
              setOpen(false);
              void signOut();
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
